import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export function useMarketplaceWishlist(skus: string[]) {
  const { user } = useAuth();
  const [wishlistedSkus, setWishlistedSkus] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const skuKey = useMemo(
    () => Array.from(new Set(skus.filter(Boolean))).sort().join('|'),
    [skus]
  );

  useEffect(() => {
    let cancelled = false;

    const loadWishlist = async () => {
      if (!user || !skuKey) {
        setWishlistedSkus([]);
        return;
      }

      setLoading(true);

      const normalizedSkus = skuKey.split('|');
      const { data, error } = await supabase
        .from('marketplace_wishlist')
        .select('sku')
        .in('sku', normalizedSkus);

      if (cancelled) {
        return;
      }

      if (error) {
        setWishlistedSkus([]);
        setLoading(false);
        return;
      }

      setWishlistedSkus(((data ?? []) as Array<{ sku: string }>).map((item) => item.sku));
      setLoading(false);
    };

    void loadWishlist();

    return () => {
      cancelled = true;
    };
  }, [skuKey, user]);

  const toggleWishlist = useCallback(
    async (sku: string) => {
      if (!user) {
        throw new Error('Authentication required');
      }

      const isWishlisted = wishlistedSkus.includes(sku);

      if (isWishlisted) {
        const { error } = await supabase
          .from('marketplace_wishlist')
          .delete()
          .eq('user_id', user.id)
          .eq('sku', sku);

        if (error) {
          throw error;
        }

        setWishlistedSkus((current) => current.filter((currentSku) => currentSku !== sku));
        return false;
      }

      const { error } = await (supabase.from('marketplace_wishlist') as any).insert({
        user_id: user.id,
        sku,
      });

      if (error) {
        throw error;
      }

      setWishlistedSkus((current) => Array.from(new Set([...current, sku])));
      return true;
    },
    [user, wishlistedSkus]
  );

  return {
    loading,
    wishlistedSkus: useMemo(() => new Set(wishlistedSkus), [wishlistedSkus]),
    toggleWishlist,
  };
}